import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'customer',
    service_type: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? `${API}/auth/login` : `${API}/auth/register`;
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(endpoint, payload);
      const { token, user } = response.data;

      toast.success(isLogin ? 'Login successful!' : 'Registration successful!');
      onLogin(token, user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (role) => {
    setLoading(true);
    try {
      // Seed data first
      await axios.post(`${API}/seed-data`);
      
      // Demo credentials
      const demoCredentials = {
        admin: { email: 'admin@physiomobile.com', password: 'admin123', role: 'admin' },
        professional: { email: 'nurul@physiomobile.com', password: 'prof123', role: 'professional' },
        customer: { email: 'ahmad@physiomobile.com', password: 'customer123', role: 'customer' }
      };

      const creds = demoCredentials[role];
      
      // Try login first, if fails then register
      try {
        const response = await axios.post(`${API}/auth/login`, {
          email: creds.email,
          password: creds.password
        });
        const { token, user } = response.data;
        toast.success(`Demo ${role} login successful!`);
        onLogin(token, user);
      } catch (loginError) {
        // If login fails, register
        const registerData = {
          email: creds.email,
          password: creds.password,
          full_name: role === 'admin' ? 'Admin User' : role === 'professional' ? 'Dr. Nurul Aisyah binti Ahmad' : 'Ahmad Firdaus bin Rosli',
          role: creds.role,
          service_type: role === 'professional' ? 'Physio' : undefined
        };
        const response = await axios.post(`${API}/auth/register`, registerData);
        const { token, user } = response.data;
        toast.success(`Demo ${role} account created!`);
        onLogin(token, user);
      }
    } catch (error) {
      toast.error('Demo login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-8">
        {/* Left Side - Branding */}
        <div className="hidden md:flex flex-col justify-center bg-gradient-to-br from-[#D32F2F] to-[#B71C1C] rounded-2xl p-12 text-white">
          <h1 className="text-5xl font-bold mb-4 tracking-tight">Physiomobile</h1>
          <p className="text-xl text-red-50 mb-8">
            Modern Healthcare Platform for Multi-Service Therapy
          </p>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold">Physiotherapy</p>
                <p className="text-sm text-red-100">Physical rehabilitation & injury recovery</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold">Occupational Therapy</p>
                <p className="text-sm text-red-100">Functional independence support</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold">Speech Therapy</p>
                <p className="text-sm text-red-100">Communication enhancement</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold">Diet & Nutrition</p>
                <p className="text-sm text-red-100">Personalized nutrition plans</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold">Care & Companion</p>
                <p className="text-sm text-red-100">Professional caregiving services</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex flex-col justify-center">
          <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              {isLogin ? 'Welcome Back' : 'Get Started'}
            </h2>
            <p className="text-slate-600 mb-8">
              {isLogin ? 'Sign in to your account' : 'Create your account'}
            </p>

            {/* Demo Login Buttons */}
            <div className="mb-6">
              <p className="text-sm text-slate-600 mb-3">Quick Demo Access:</p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => handleDemoLogin('admin')}
                  disabled={loading}
                  data-testid="demo-admin-btn"
                  className="px-3 py-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                >
                  Admin
                </button>
                <button
                  type="button"
                  onClick={() => handleDemoLogin('professional')}
                  disabled={loading}
                  data-testid="demo-professional-btn"
                  className="px-3 py-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                >
                  Professional
                </button>
                <button
                  type="button"
                  onClick={() => handleDemoLogin('customer')}
                  disabled={loading}
                  data-testid="demo-customer-btn"
                  className="px-3 py-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                >
                  Customer
                </button>
              </div>
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">Or continue with</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="input-field w-full"
                    required
                    data-testid="full-name-input"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-field w-full"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-field w-full"
                  required
                  data-testid="password-input"
                />
              </div>

              {!isLogin && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Role
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="input-field w-full"
                      data-testid="role-select"
                    >
                      <option value="customer">Customer</option>
                      <option value="professional">Professional</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  {formData.role === 'professional' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Service Type
                      </label>
                      <select
                        value={formData.service_type}
                        onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                        className="input-field w-full"
                        required
                        data-testid="service-type-select"
                      >
                        <option value="">Select Service</option>
                        <option value="Physio">Physiotherapy</option>
                        <option value="OT">Occupational Therapy</option>
                        <option value="Speech">Speech Therapy</option>
                        <option value="Diet">Diet & Nutrition</option>
                        <option value="Care">Care & Companion</option>
                      </select>
                    </div>
                  )}
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full mt-6"
                data-testid="submit-btn"
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-[#D32F2F] hover:text-[#B71C1C] font-medium"
                data-testid="toggle-auth-mode-btn"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
